/**
 * BudgetViewHeader Component
 *
 * Header principal da visualização de orçamento com status, informações básicas e ações
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  Edit,
  Copy,
  Mail,
  Printer,
  FileText,
  Download,
  Share2,
  MoreHorizontal,
  Eye,
  Clock,
  Calendar,
  MessageSquare,
  Signature,
  ExternalLink,
  RefreshCw,
  Trash2,
  History,
  Bell,
  Zap
} from 'lucide-react';
import { BudgetStatusBadge, BudgetStatus } from '../BudgetStatusBadge';

interface BudgetViewHeaderProps {
  budget: {
    id: string;
    number: string;
    status: BudgetStatus;
    createdAt: string;
    validUntil: string;
    client: {
      name: string;
      email: string;
    };
    total: number;
    version?: number;
  };
  onStatusChange?: (newStatus: BudgetStatus, justification?: string) => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onSend?: (type: 'email' | 'whatsapp') => void;
  onExport?: (format: 'pdf' | 'excel') => void;
  onPrint?: () => void;
  onPreview?: () => void;
  onShare?: () => void;
  onConvert?: () => void;
  onAddComment?: () => void;
  onRemind?: () => void;
  showVersionHistory?: boolean;
  className?: string;
}

const STATUS_CHANGE_REASONS = {
  approved: [
    'Cliente aprovou por email',
    'Cliente aprovou por telefone',
    'Aprovação verbal',
    'Aprovação por assinatura digital',
    'Outro'
  ],
  rejected: [
    'Preço acima do esperado',
    'Escopo não atende necessidade',
    'Prazo de entrega',
    'Condições de pagamento',
    'Fornecedor concorrente',
    'Outro'
  ],
  sent: [
    'Envio inicial',
    'Reenvio após alterações',
    'Envio para aprovação final',
    'Outro'
  ]
} as const;

export function BudgetViewHeader({
  budget,
  onStatusChange,
  onEdit,
  onDuplicate,
  onDelete,
  onSend,
  onExport,
  onPrint,
  onPreview,
  onShare,
  onConvert,
  onAddComment,
  onRemind,
  showVersionHistory = false,
  className
}: BudgetViewHeaderProps) {
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<BudgetStatus>();
  const [justification, setJustification] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  const handleStatusChange = () => {
    if (newStatus && onStatusChange) {
      const finalJustification = selectedReason === 'Outro' ? customReason : selectedReason;
      onStatusChange(newStatus, finalJustification || justification);
      setStatusDialogOpen(false);
      setNewStatus(undefined);
      setJustification('');
      setCustomReason('');
      setSelectedReason('');
    }
  };

  const isExpired = new Date(budget.validUntil) < new Date();
  const canConvert = budget.status === 'approved' && !isExpired;

  return (
    <>
      <div className={`space-y-4 ${className}`}>
        {/* Top Row - Navigation and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/services/budgets">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </Link>
            {showVersionHistory && budget.version && (
              <Badge variant="outline" className="text-xs">
                v{budget.version}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Primary Actions */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSend?.('email')}
              className="hidden sm:flex"
            >
              <Mail className="h-4 w-4 mr-2" />
              Enviar
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onPrint}
              className="hidden sm:flex"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onSend?.('email')}>
                  <Mail className="h-4 w-4 mr-2" />
                  Enviar por Email
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onSend?.('whatsapp')}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Enviar por WhatsApp
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onPreview}>
                  <Eye className="h-4 w-4 mr-2" />
                  Visualizar PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport?.('pdf')}>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport?.('excel')}>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar Link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicar Orçamento
                </DropdownMenuItem>
                {canConvert && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onConvert} className="text-green-600">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Converter em Venda
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onAddComment}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Adicionar Comentário
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onRemind}>
                  <Bell className="h-4 w-4 mr-2" />
                  Enviar Lembrete
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Orçamento
                </DropdownMenuItem>
                {showVersionHistory && (
                  <DropdownMenuItem>
                    <History className="h-4 w-4 mr-2" />
                    Histórico de Versões
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="text-red-600" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Orçamento
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Budget Title and Status */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold">Orçamento {budget.number}</h1>
              <BudgetStatusBadge status={budget.status} animated={budget.status === 'sent'} />
              {isExpired && (
                <Badge variant="destructive" className="animate-pulse">
                  <Clock className="h-3 w-3 mr-1" />
                  Expirado
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Criado em {new Date(budget.createdAt).toLocaleDateString('pt-BR')}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Válido até {new Date(budget.validUntil).toLocaleDateString('pt-BR')}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            {budget.status === 'draft' && (
              <Button onClick={() => setNewStatus('sent')} className="bg-blue-600 hover:bg-blue-700">
                <Send className="h-4 w-4 mr-2" />
                Enviar para Cliente
              </Button>
            )}
            {budget.status === 'sent' && (
              <Button onClick={() => setNewStatus('approved')} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Marcar como Aprovado
              </Button>
            )}
            {canConvert && (
              <Button onClick={onConvert} className="bg-purple-600 hover:bg-purple-700">
                <RefreshCw className="h-4 w-4 mr-2" />
                Converter em Venda
              </Button>
            )}
            <Button variant="outline" onClick={onPreview}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </div>
        </div>

        {/* Client and Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Cliente</span>
            </div>
            <p className="font-semibold">{budget.client.name}</p>
            <p className="text-sm text-muted-foreground">{budget.client.email}</p>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Validade</span>
            </div>
            <p className="font-semibold">
              {isExpired ? 'Expirado' : `${Math.ceil((new Date(budget.validUntil).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} dias`}
            </p>
            <p className="text-sm text-muted-foreground">
              {new Date(budget.validUntil).toLocaleDateString('pt-BR')}
            </p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Valor Total</span>
            </div>
            <p className="font-bold text-lg text-primary">
              {budget.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p className="text-sm text-muted-foreground">
              {budget.items?.length || 0} item(s)
            </p>
          </div>
        </div>

        {/* Status Change Alert */}
        {newStatus && (
          <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Alterar status para {newStatus === 'approved' ? 'Aprovado' : newStatus === 'sent' ? 'Enviado' : 'Rejeitado'}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação será registrada no histórico do orçamento e notificará o cliente se aplicável.
                </AlertDialogDescription>
              </AlertDialogHeader>

              {(STATUS_CHANGE_REASONS[newStatus as keyof typeof STATUS_CHANGE_REASONS]) && (
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Motivo da alteração</Label>
                    <select
                      className="w-full mt-1 p-2 border rounded-md bg-background text-foreground"
                      value={selectedReason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                    >
                      <option value="">Selecione um motivo...</option>
                      {STATUS_CHANGE_REASONS[newStatus as keyof typeof STATUS_CHANGE_REASONS]?.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedReason === 'Outro' && (
                    <div>
                      <Label>Descreva o motivo</Label>
                      <Textarea
                        placeholder="Informe o motivo detalhadamente..."
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                      />
                    </div>
                  )}

                  <div>
                    <Label>Observações adicionais (opcional)</Label>
                    <Textarea
                      placeholder="Adicione qualquer informação relevante..."
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleStatusChange}>
                  Confirmar Alteração
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </>
  );
}